import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import {
  Users,
  Plus,
  Upload,
  Search,
  MoreVertical,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Phone,
  Mail,
  Tag,
  MapPin,
  DollarSign,
  Calendar,
  UserX,
  Trash2,
  CheckCircle
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomersPage() {
  const location = useLocation();
  const { getToken } = useTenant();
  const dashData = useDashboardData();
  const preloaded = dashData?.customersPage;
  const [customers, setCustomers] = useState(preloaded?.customers || []);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(preloaded?.total_pages || 1);
  const [total, setTotal] = useState(preloaded?.total || 0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'subscribed' | 'unsubscribed'

  // Customer counts for stat cards
  const [customerCounts, setCustomerCounts] = useState(null);

  // Dismissable unsub banner
  const [unsubBannerDismissed, setUnsubBannerDismissed] = useState(false);

  // Recent unsubscribes banner
  const [recentUnsubs, setRecentUnsubs] = useState(dashData?.recentUnsubscribes || null);
  const skipInitialFetch = useRef(!!preloaded);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(location.state?.action === 'import');
  const [showEditModal, setShowEditModal] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    first_name: '',
    last_name: '',
    suburb: '',
    total_amount_spent: '',
    last_order_at: '',
    tags: '',
    opt_in_status: true,
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

  const executeDeleteAll = async () => {
    setShowDeleteAllModal(false);
    setDeleting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/customers/all?confirm=yes`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      const data = await res.json();
      setCustomers([]);
      setTotal(0);
      setTotalPages(1);
      setPage(1);
    } catch (err) {
      setError('Failed to delete: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20',
      });
      if (search) params.set('search', search);
      if (statusFilter === 'subscribed') params.set('opt_in_only', 'true');
      if (statusFilter === 'unsubscribed') {
        params.set('opt_out_only', 'true');
        params.set('sort', 'opt_out_at_desc');
      }

      const response = await fetch(`${BACKEND_URL}/api/customers?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch customers');

      const data = await response.json();
      setCustomers(data.customers);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, search, statusFilter]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchCustomers();
  }, [fetchCustomers]);

  // Fetch recent unsubscribes for the banner (skip if preloaded)
  const skipUnsubFetch = useRef(!!dashData?.recentUnsubscribes);
  useEffect(() => {
    if (skipUnsubFetch.current) {
      skipUnsubFetch.current = false;
      return;
    }
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/customers/recent-unsubscribes?days=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setRecentUnsubs(await res.json());
      } catch {}
    })();
  }, [getToken]);

  // Fetch customer counts for stat cards
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}/api/customers/count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setCustomerCounts(await res.json());
      } catch {}
    })();
  }, [getToken]);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          total_amount_spent_cents: formData.total_amount_spent ? Math.round(parseFloat(formData.total_amount_spent) * 100) : 0,
          last_order_at: formData.last_order_at || null,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add customer');
      }

      setShowAddModal(false);
      setFormData({ phone: '', email: '', first_name: '', last_name: '', suburb: '', total_amount_spent: '', last_order_at: '', tags: '', opt_in_status: true });
      fetchCustomers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/customers/${showEditModal.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email || null,
          first_name: formData.first_name || null,
          last_name: formData.last_name || null,
          suburb: formData.suburb || null,
          total_amount_spent_cents: formData.total_amount_spent ? Math.round(parseFloat(formData.total_amount_spent) * 100) : null,
          last_order_at: formData.last_order_at || null,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
          opt_in_status: formData.opt_in_status,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update customer');
      }

      setShowEditModal(null);
      fetchCustomers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This cannot be undone.')) return;
    setFormLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete customer');
      }
      setShowEditModal(null);
      fetchCustomers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setImportLoading(true);
    setImportResult(null);

    try {
      const token = await getToken();

      // Step 1: Get presigned upload URL
      const presignRes = await fetch(`${BACKEND_URL}/api/files/presigned-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_name: importFile.name,
          mime_type: 'text/csv',
          purpose: 'customer_import',
          file_size_bytes: importFile.size,
        }),
      });

      if (!presignRes.ok) throw new Error('Step 1 failed: could not get upload URL');
      const { upload_url, file_id } = await presignRes.json();

      // Step 2: Upload file directly to R2
      const uploadRes = await fetch(upload_url, {
        method: 'PUT',
        body: importFile,
        headers: { 'Content-Type': 'text/csv' },
      });

      if (!uploadRes.ok) throw new Error(`Step 2 failed: R2 upload returned ${uploadRes.status}`);

      // Step 3: Confirm upload
      const confirmRes = await fetch(`${BACKEND_URL}/api/files/confirm-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_id }),
      });

      if (!confirmRes.ok) throw new Error('Step 3 failed: could not confirm upload');

      // Step 4: Import customers
      const importRes = await fetch(`${BACKEND_URL}/api/customers/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_id }),
      });

      if (!importRes.ok) {
        const err = await importRes.json();
        throw new Error(err.detail || 'Import failed');
      }

      const result = await importRes.json();
      setImportResult(result);
      fetchCustomers();
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      setImportLoading(false);
    }
  };

  const handleToggleOptIn = async (customer) => {
    const newStatus = !customer.opt_in_status;
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/customers/${customer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ opt_in_status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update');
      // Update locally for instant feedback
      setCustomers(prev => prev.map(c =>
        c.id === customer.id ? { ...c, opt_in_status: newStatus, opt_out_at: newStatus ? null : new Date().toISOString() } : c
      ));
      // Refresh unsub banner
      const bannerRes = await fetch(`${BACKEND_URL}/api/customers/recent-unsubscribes?days=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (bannerRes.ok) setRecentUnsubs(await bannerRes.json());
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditModal = (customer) => {
    setFormData({
      phone: customer.phone,
      email: customer.email || '',
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      suburb: customer.suburb || '',
      total_amount_spent: customer.total_amount_spent_cents ? (customer.total_amount_spent_cents / 100).toFixed(2) : '',
      last_order_at: customer.last_order_at ? customer.last_order_at.split('T')[0] : '',
      tags: (customer.tags || []).join(', '),
      opt_in_status: customer.opt_in_status,
    });
    setFormError(null);
    setShowEditModal(customer);
  };

  return (
    <div className="p-6 lg:p-8" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        </div>
        <div className="flex gap-3">
          {total > 0 && (
            <button
              onClick={() => setShowDeleteAllModal(true)}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting...' : 'Delete All'}
            </button>
          )}
          <OnboardingTooltip id="customers_import" text="Import your customer list from a CSV file here">
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </button>
          </OnboardingTooltip>
          <button
            onClick={() => {
              setFormData({ phone: '', email: '', first_name: '', last_name: '', suburb: '', total_amount_spent: '', last_order_at: '', tags: '', opt_in_status: true });
              setFormError(null);
              setShowAddModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            data-testid="add-customer-btn"
          >
            <Plus className="w-4 h-4" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Customer Stats Cards */}
      {customerCounts && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => { setStatusFilter('subscribed'); setPage(1); }}
            className={`text-left rounded-xl border p-4 transition-all ${statusFilter === 'subscribed' ? 'ring-2 ring-green-500 border-green-300 bg-green-50' : 'border-gray-100 bg-white hover:shadow-md'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-gray-500">Subscribed</span>
            </div>
            <div className="text-3xl font-bold text-green-700">{customerCounts.opted_in}</div>
            <div className="text-xs text-gray-400 mt-0.5">Active SMS recipients</div>
          </button>
          <button
            onClick={() => { setStatusFilter('all'); setPage(1); }}
            className={`text-left rounded-xl border p-4 transition-all ${statusFilter === 'all' ? 'ring-2 ring-blue-500 border-blue-300 bg-blue-50' : 'border-gray-100 bg-white hover:shadow-md'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-blue-500" />
              <span className="text-sm font-medium text-gray-500">Total</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">{customerCounts.total}</div>
            <div className="text-xs text-gray-400 mt-0.5">All customers</div>
          </button>
          <button
            onClick={() => { setStatusFilter('unsubscribed'); setPage(1); }}
            className={`text-left rounded-xl border p-4 transition-all ${statusFilter === 'unsubscribed' ? 'ring-2 ring-red-500 border-red-300 bg-red-50' : 'border-gray-100 bg-white hover:shadow-md'}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <UserX className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-gray-500">Unsubscribed</span>
            </div>
            <div className="text-2xl font-bold text-red-700">{customerCounts.opted_out}</div>
            <div className="text-xs text-gray-400 mt-0.5">Opted out of SMS</div>
          </button>
        </div>
      )}

      {/* Recent Unsubscribes Banner */}
      {recentUnsubs && recentUnsubs.count > 0 && statusFilter !== 'unsubscribed' && !unsubBannerDismissed && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <UserX className="w-5 h-5 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-red-800">
                {recentUnsubs.count} customer{recentUnsubs.count !== 1 ? 's' : ''} unsubscribed in the last 30 days
              </h3>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-red-700">
                {recentUnsubs.recent.map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1">
                    <span className="font-medium">{c.first_name || c.phone}</span>
                    {c.opt_out_at && (
                      <span className="text-red-500 text-xs">
                        {new Date(c.opt_out_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </span>
                ))}
                {recentUnsubs.count > 5 && (
                  <span className="text-red-500 text-xs">+{recentUnsubs.count - 5} more</span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={() => { setStatusFilter('unsubscribed'); setPage(1); }}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
              >
                View all
              </button>
              <button
                onClick={() => setUnsubBannerDismissed(true)}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by phone, email, or name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            data-testid="search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="all">All customers</option>
          <option value="subscribed">Subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="divide-y divide-gray-100">
            {[1,2,3,4,5].map(i => <div key={i} className="p-4 flex items-center gap-4"><Skeleton className="h-4 w-4" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-28" /><Skeleton className="h-4 w-20" /></div>)}
          </div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers yet</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first customer or importing a CSV file.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suburb</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50" data-testid={`customer-row-${customer.id}`}>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {customer.first_name || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {customer.last_name || '-'}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1 text-sm text-gray-900">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          {customer.phone}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {customer.email ? (
                          <div className="flex items-center gap-1 text-sm text-gray-500">
                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate max-w-32">{customer.email}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {customer.suburb ? (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            {customer.suburb}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-sm font-medium text-gray-900">
                          ${((customer.total_amount_spent_cents || 0) / 100).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {customer.last_order_at ? (
                          <span className="text-sm text-gray-600">
                            {new Date(customer.last_order_at).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(customer.tags || []).slice(0, 2).map((tag, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                          {(customer.tags || []).length > 2 && (
                            <span className="text-xs text-gray-400">+{customer.tags.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {customer.opt_in_status ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <Check className="w-3 h-3" />
                            Subscribed
                          </span>
                        ) : (
                          <div>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <X className="w-3 h-3" />
                              Unsubscribed
                            </span>
                            {customer.opt_out_at && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(customer.opt_out_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {customer.opt_in_status ? (
                            <button
                              onClick={() => handleToggleOptIn(customer)}
                              className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Unsubscribe"
                            >
                              Unsub
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleOptIn(customer)}
                              className="px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Re-subscribe"
                            >
                              Re-sub
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(customer)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" data-testid="add-customer-modal">
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Add Customer</h2>
            </div>
            <form onSubmit={handleAddCustomer} className="p-6 space-y-4 overflow-y-auto flex-1">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0412 345 678"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                <input
                  type="text"
                  value={formData.suburb}
                  onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                  placeholder="e.g., Richmond"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount Spent ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_amount_spent}
                    onChange={(e) => setFormData({ ...formData, total_amount_spent: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Order Date</label>
                  <input
                    type="date"
                    value={formData.last_order_at}
                    onChange={(e) => setFormData({ ...formData, last_order_at: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="vip, regular"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.opt_in_status}
                  onChange={(e) => setFormData({ ...formData, opt_in_status: e.target.checked })}
                  className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Customer has opted in to receive SMS</span>
              </label>
              <div className="flex gap-3 pt-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                  data-testid="submit-customer-btn"
                >
                  {formLoading ? 'Adding...' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" data-testid="edit-customer-modal">
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Edit Customer</h2>
            </div>
            <form onSubmit={handleUpdateCustomer} className="p-6 space-y-4 overflow-y-auto flex-1">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  disabled
                  value={formData.phone}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                <input
                  type="text"
                  value={formData.suburb}
                  onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                  placeholder="e.g., Richmond"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount Spent ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_amount_spent}
                    onChange={(e) => setFormData({ ...formData, total_amount_spent: e.target.value })}
                    placeholder="0.00"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Order Date</label>
                  <input
                    type="date"
                    value={formData.last_order_at}
                    onChange={(e) => setFormData({ ...formData, last_order_at: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.opt_in_status}
                  onChange={(e) => setFormData({ ...formData, opt_in_status: e.target.checked })}
                  className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">Customer has opted in to receive SMS</span>
              </label>
              <div className="flex gap-3 pt-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleDeleteCustomer(showEditModal.id)}
                  disabled={formLoading}
                  className="px-4 py-2.5 border border-red-200 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4 inline mr-1" />
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" data-testid="import-modal">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Import Customers from CSV</h2>
            </div>
            <div className="p-6 space-y-4">
              {importResult?.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {importResult.error}
                </div>
              )}
              
              {importResult && !importResult.error && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-medium text-green-800 mb-2">Import Complete</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>Total rows: {importResult.total_rows}</li>
                    <li>Imported: {importResult.imported}</li>
                    {importResult.already_existing > 0 && (
                      <li>Already existing: {importResult.already_existing}</li>
                    )}
                    {importResult.duplicates_in_file > 0 && (
                      <li>Duplicates in file: {importResult.duplicates_in_file}</li>
                    )}
                    {importResult.errors?.length > 0 && (
                      <li className="text-red-600">Errors: {importResult.errors.length}</li>
                    )}
                  </ul>
                </div>
              )}

              {!importResult && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                    <p className="font-medium text-gray-900 mb-2">Upload any CSV containing phone numbers.</p>
                    <p className="mb-2">We'll auto-detect columns and clean the data:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Australian mobile numbers extracted & formatted</li>
                      <li>Landlines and invalid numbers removed</li>
                      <li>Duplicates filtered out</li>
                      <li>Names and emails matched if available</li>
                    </ul>
                  </div>

                  <div
                    className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-orange-300 transition-colors"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-orange-400', 'bg-orange-50'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50');
                      const file = e.dataTransfer.files?.[0];
                      if (file && file.name.endsWith('.csv')) setImportFile(file);
                    }}
                  >
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label htmlFor="csv-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      {importFile ? (
                        <p className="text-gray-900 font-medium">{importFile.name}</p>
                      ) : (
                        <>
                          <p className="text-gray-900 font-medium">Click to upload CSV</p>
                          <p className="text-sm text-gray-500 mt-1">or drag and drop</p>
                        </>
                      )}
                    </label>
                  </div>
                </>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setImportFile(null);
                    setImportResult(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {importResult ? 'Close' : 'Cancel'}
                </button>
                {!importResult && (
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                    className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    data-testid="import-submit-btn"
                  >
                    {importLoading ? 'Importing...' : 'Import'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete All confirmation modal */}
      <AlertDialog open={showDeleteAllModal} onOpenChange={setShowDeleteAllModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all customers?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  This will permanently delete <strong>{total.toLocaleString()} customers</strong> from your account. This action cannot be undone.
                </p>
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-red-700">
                    All customer data, tags, and history will be permanently removed.
                  </p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeDeleteAll}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete All {total.toLocaleString()} Customers
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
